import classNames from "classnames";
import { useAtomValue, useSetAtom } from "jotai";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";

import { heroScrollHintShownState, heroScrollHintVisibleState } from "~/atoms/hero-scroll-hint";
import AppRoutes from "~/utils/app-routes";
import CommonUtils from "~/utils/common-utils";

import styles from "./hero-scroll-hint.module.scss";

const SCROLL_INDICATOR_SRC = "/next/assets/hero-scroll-hint/scroll-indicator.svg";

type HorizontalZone = "left" | "center" | "right";
type VerticalZone = "top" | "center" | "bottom";

type Props = {
  isActive?: boolean;
  horizontalZone?: HorizontalZone;
  verticalZone?: VerticalZone;
};

// Horizontal takes priority over vertical; "center" in either axis falls through to defaultPosition.
const HORIZONTAL_ZONE_CLASSNAMES: Partial<Record<HorizontalZone, string>> = {
  left: styles.zoneLeft,
  right: styles.zoneRight,
};

const VERTICAL_ZONE_CLASSNAMES: Partial<Record<VerticalZone, string>> = {
  bottom: styles.zoneBottom,
  top: styles.zoneTop,
};

// Rendered as a child of the slide's panel (or, when there's no panel, of the slide itself) -
// one instance per slide, remounted on every swiper autoplay rotation. Purely presentational:
// Hero owns the shared timers via a single useHeroScrollHintScheduler call, this just renders
// that state for whichever slide happens to be isActive.
export default function HeroScrollHint({ isActive = true, horizontalZone, verticalZone }: Props) {
  const t = useTranslations();
  const router = useRouter();
  // router.pathname is in sync with the route being rendered right now, unlike pageProps
  // (via useServerSideProps), which can still reflect the previous page for a render or two
  // during a client-side navigation.
  const showScrollHint =
    !CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_DISABLE_HERO_HINT) && router.pathname === AppRoutes.HOMEPAGE;

  const visible = useAtomValue(heroScrollHintVisibleState);
  const setVisible = useSetAtom(heroScrollHintVisibleState);
  const setShown = useSetAtom(heroScrollHintShownState);

  // The button can end up nested inside the panel's Link (when the slide has a link), so
  // without stopping propagation the click would also trigger the Link's navigation.
  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShown(true);
    setVisible(false);
    window.scrollBy({ top: window.innerHeight, behavior: "smooth" });
  };

  const zoneClassName =
    (horizontalZone && HORIZONTAL_ZONE_CLASSNAMES[horizontalZone]) ||
    (verticalZone && VERTICAL_ZONE_CLASSNAMES[verticalZone]) ||
    styles.defaultPosition;

  if (!showScrollHint) {
    return null;
  }

  // isActive gates whether THIS slide is the one currently on screen, so of the N
  // mounted instances only the active slide's one ever actually shows.
  const showHint = visible && isActive;

  return (
    <div className={classNames(styles.container, zoneClassName, showHint ? styles.visible : undefined)}>
      <button
        type="button"
        className={styles.button}
        onClick={handleClick}
        aria-label={t("generic.heroScrollHintAriaLabel")}
        aria-hidden={!showHint}
        tabIndex={showHint ? 0 : -1}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- SMIL-animated svg must bypass next/image optimization */}
        <img src={SCROLL_INDICATOR_SRC} alt="" className={styles.icon} />
      </button>
    </div>
  );
}
